import { useEffect, useState, useContext, useRef } from 'react';
import AppContext from '../contexts/AppContext';
import ConfigContext from '../contexts/ConfigContext';
import globalCountries from './../data/globalCountries';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import Button from '../components/Button';
import DividerLine from '../components/DividerLine';

import iconCaret from './../images/success/icon-caret.svg';
import iconFacebook from './../images/success/facebook.svg';
import iconYoutube from './../images/success/youtube.svg';
import iconTwitter from './../images/success/twitter.svg';
import iconLinkedIn from './../images/success/linkedin.svg';

import iconInstallers from './../images/success/icon_global_certified_installers.svg';
import iconAdvice from './../images/success/icon_global_advice.svg';
import iconInstaller from './../images/success/icon_global_installer.svg';

import iconSquare from './../images/square.svg';
import iconSquareChecked from './../images/square_check.svg';

import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { postcodeValidator } from 'postcode-validator';
import Autocomplete from 'react-google-autocomplete';

import { UAParser } from 'ua-parser-js';
import { useTranslation } from 'react-i18next';

declare const sp: any;
declare const Zuko: any;
declare const visitData: any;

export default function GlobalFunnel() {
  const { setLoadedHome } = useContext(AppContext);
  const { country, countryLanguage } = useContext(ConfigContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const phoneUtil = PhoneNumberUtil.getInstance();

  const getCountryPhoneCode = (countryCode: string): string | null => {
    const countryData = globalCountries.find(
      (country) => country.code === countryCode
    );
    return countryData ? countryData.phoneCode : '';
  };

  const countryPhoneCode = getCountryPhoneCode(country);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('global-funnel');

    setLoadedHome(true);

    let sourceData;
    try {
      sourceData = visitData.get();
    } catch (e: any) {
      sourceData = { source: 'direct', medium: 'none' };
    }

    const { device } = UAParser();
    Zuko.trackForm({
      target: document.querySelector('#root > div'),
      slug: process.env[`REACT_APP_ZUKO_SLUG_${country}`] || '',
    })
      .setAttribute('traffic-source', sourceData.source)
      .setAttribute('traffic-medium', sourceData.medium)
      .setAttribute(
        'device-model',
        !device.vendor && !device.model
          ? 'Unknown'
          : `${device.vendor ? device.vendor + ' ' : ''}${
              device.model ? device.model : ''
            }`
      )
      .trackEvent(Zuko.FORM_VIEW_EVENT);
  }, []);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [postcode, setPostcode] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [addressComponents, setAddressComponents]: [
    addressComponents: AddressComponents,
    setAddressComponents: (components: AddressComponents) => void
  ] = useState({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [addressHaveError, setAddressHaveError] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorSubmitting, setErrorSubmitting] = useState(false);

  const setPostcodeUppercase = (code: string) => {
    setPostcode(code.toUpperCase());
  };

  const isValidName = (value: string): boolean => {
    return value.trim().length >= 2;
  };

  const isValidEmail = (email: string): boolean => {
    return !!String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )?.length;
  };

  const isValidPhone = (phone: string) => {
    try {
      return phoneUtil.isValidNumber(
        phoneUtil.parseAndKeepRawInput(`+${countryPhoneCode}${phone}`)
      );
    } catch (error) {
      return false;
    }
  };

  const isValidPostcode = (code: string): boolean => {
    return postcodeValidator(code, country);
  };

  const isFormValid = (): boolean => {
    return (
      isValidName(firstName) &&
      isValidName(lastName) &&
      isValidPhone(phone) &&
      (isValidPostcode(postcode) || !addressHaveError) &&
      isValidEmail(email) &&
      acceptedTerms
    );
  };

  return (
    <Layout
      addPadding={false}
      className="text-[14px] md:text-[16px] text-black relative min-h-[100vh] pb-[195px]">
      <div className="pt-[40px] pb-[40px] md:py-40 md: px-6 md:px-20">
        <div className="max-w-[1400px] m-auto flex flex-col md:flex-row gap-10">
          <div className="basis-3/5">
            <p className="text-[28px] short:text-[28px] lg:text-[56px] leading-tight mb-[30px] lg:mb-[50px]">
              {t('globalFunnel.home.title')}
            </p>
            <div className="text-[16px] short:text-[16px] lg:text-[20px]">
              <div className="flex items-center my-4">
                <img
                  src={iconInstallers}
                  alt=""
                  className="w-[30px] h-[30px] short:w-[30px] short:h-[30px] lg:w-12 lg:h-12 mr-3"
                />
                <p>{t('globalFunnel.home.content1')}</p>
              </div>
              <div className="flex items-center my-4">
                <img
                  src={iconAdvice}
                  alt=""
                  className="w-[30px] h-[30px] short:w-[30px] short:h-[30px] lg:w-12 lg:h-12 mr-3"
                />
                <p>{t('globalFunnel.home.content2')}</p>
              </div>
              <div className="flex items-center my-4">
                <img
                  src={iconInstaller}
                  alt=""
                  className="w-[30px] h-[30px] short:w-[30px] short:h-[30px] lg:w-12 lg:h-12 mr-3"
                />
                <p>{t('globalFunnel.home.content3')}</p>
              </div>
            </div>
          </div>

          <div className="basis-2/5">
            <form className="w-full">
              <div className="flex mb-[1px] gap-[1px] md:mb-1 md:gap-1 flex-col md:flex-row">
                <div className="w-full md:w-1/2 p-6 bg-white rounded-t-xl md:rounded-tl-xl md:rounded-tr-none">
                  <FormInput
                    value={firstName}
                    onChange={setFirstName}
                    haveError={!isValidName(firstName)}
                    id="first-name"
                    labelKey="firstName"
                    placeholderKey="firstNamePlaceholder"
                    zukoId="input-first-name"></FormInput>
                </div>
                <div className="w-full md:w-1/2 p-6 bg-white rounded-none md:rounded-tr-xl">
                  <FormInput
                    value={lastName}
                    onChange={setLastName}
                    haveError={!isValidName(lastName)}
                    id="last-name"
                    labelKey="lastName"
                    placeholderKey="lastNamePlaceholder"
                    zukoId="input-last-name"></FormInput>
                </div>
              </div>
              <div className="flex mb-[1px] gap-[1px] md:mb-1 md:gap-1 flex-col md:flex-row">
                <div className="w-full md:w-1/2 p-6 bg-white overflow-hidden">
                  <FormInput
                    type="phone"
                    value={phone}
                    onChange={setPhone}
                    haveError={!isValidPhone(phone)}
                    labelKey="phone"
                    placeholderKey="phonePlaceholder"
                    zukoId="input-phone-number"></FormInput>
                </div>
                <div className="w-full md:w-1/2 p-6 bg-white">
                  {country === 'NL' && (
                    <FormInput
                      type="email"
                      value={email}
                      onChange={setEmail}
                      haveError={!isValidEmail(email)}
                      id="email"
                      labelKey="email"
                      placeholderKey="emailPlaceholder"
                      zukoId="input-email"></FormInput>
                  )}
                  {country !== 'NL' && (
                    <FormInput
                      value={postcode}
                      onChange={setPostcodeUppercase}
                      haveError={!isValidPostcode(postcode)}
                      id="postcode"
                      labelKey="postcode"
                      placeholderKey="postcodePlaceholder"
                      zukoId="input-postcode"
                      maxLength={country === 'NL' ? 7 : 5}></FormInput>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap">
                <div className="w-full p-6 bg-white rounded-b-xl">
                  {country === 'NL' && (
                    <FormInput
                      type="address"
                      value={address}
                      onChange={setAddress}
                      haveError={addressHaveError}
                      setHaveError={setAddressHaveError}
                      id="address"
                      labelKey="address"
                      placeholderKey="addressPlaceholder"
                      zukoId="input-address"
                      setAddressComponents={setAddressComponents}></FormInput>
                  )}
                  {country !== 'NL' && (
                    <FormInput
                      type="email"
                      value={email}
                      onChange={setEmail}
                      haveError={!isValidEmail(email)}
                      id="email"
                      labelKey="email"
                      placeholderKey="emailPlaceholder"
                      zukoId="input-email"></FormInput>
                  )}
                </div>
              </div>
              {errorSubmitting && (
                <div className="text-enph-red-functional bg-enph-grey-04 mt-10 rounded-xl p-6">
                  <p>{t('globalFunnel.home.form.submitErrorMessage')}</p>
                </div>
              )}
              <div className="text-center">
                <Button
                  title={t('globalFunnel.home.form.submit')}
                  className="mt-10"
                  isLoading={isSubmitting}
                  disabled={!isFormValid() || isSubmitting}
                  action={async () => {
                    setIsSubmitting(true);
                    setErrorSubmitting(false);
                    const postData = {
                      human_check: '',
                      country,
                      first_name: firstName,
                      last_name: lastName,
                      phone_number: phone,
                      zip: postcode || addressComponents.zip,
                      email,
                      street: addressComponents.street,
                      city: addressComponents.city,
                    };
                    try {
                      const rawResponse = await fetch(
                        'https://global.solarleadfactory.ai/api/leads/add',
                        {
                          method: 'POST',
                          headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify(postData),
                        }
                      );
                      const content = await rawResponse.json();
                      if (content.status === 1) {
                        navigate(`/?lang=${countryLanguage}&pg=success`);
                      } else {
                        setErrorSubmitting(true);
                        setIsSubmitting(false);
                      }
                    } catch {
                      setErrorSubmitting(true);
                      setIsSubmitting(false);
                    }
                  }}
                  data-zuko-id="button-submit"></Button>
              </div>
            </form>
            <br></br>
            <div className="flex">
              <img
                src={acceptedTerms ? iconSquareChecked : iconSquare}
                alt=""
                className="w-[20px] h-[20px] mr-2 cursor-pointer"
                onClick={() => setAcceptedTerms(!acceptedTerms)}
                data-zuko-id="button-accept-terms"
                data-zuko-tag-name="button"
              />
              <div className="text-[14px]">
                {t('globalFunnel.home.form.terms')}{' '}
                <a
                  className="underline"
                  href={t('globalFunnel.home.form.termsLink')}
                  target="_blank"
                  rel="noreferrer">
                  {t('globalFunnel.home.form.termsLinkText')}
                </a>
                .
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer></Footer>
    </Layout>
  );
}

type AddressComponents = {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
};

const FormInput = ({
  type = 'text',
  value,
  onChange,
  haveError = false,
  setHaveError,
  id,
  labelKey,
  placeholderKey,
  zukoId,
  maxLength = 100,
  setAddressComponents,
}: {
  type?: string;
  value: string;
  onChange: (newValue: string) => void;
  haveError?: boolean;
  setHaveError?: (newValue: boolean) => void;
  id?: string;
  labelKey: string;
  placeholderKey: string;
  zukoId: string;
  maxLength?: number;
  setAddressComponents?: (components: AddressComponents) => void;
}) => {
  const { country } = useContext(ConfigContext);
  const { t } = useTranslation();
  const translateKeyPre = 'globalFunnel.home.form.';
  const [touched, setTouched] = useState(false);
  const googleAutocompleteRef: any = useRef(null);

  return (
    <>
      <label
        className={`block tracking-wide mb-2 after:content-['*'] after:text-[#de2100] after:ml-[2px] ${
          touched && haveError ? 'text-[#de2100]' : 'text-gray-700'
        }`}
        htmlFor={id}>
        {t(`${translateKeyPre}${labelKey}`)}
      </label>
      {type === 'phone' ? (
        <PhoneInput
          defaultCountry={country.toLowerCase()}
          value={value}
          onChange={(phone) => onChange(phone)}
          placeholder={t(`${translateKeyPre}${placeholderKey}`)}
          hideDropdown={true}
          disableDialCodeAndPrefix={true}
          showDisabledDialCodeAndPrefix={true}
          data-zuko-id={zukoId}
          inputProps={{ onBlur: () => setTouched(true) }}
          className={`${
            touched && haveError ? 'border-b border-b-[#de2100]' : 'border-none'
          }`}
        />
      ) : type === 'address' ? (
        <Autocomplete
          placeholder={t(`${translateKeyPre}${placeholderKey}`)}
          defaultValue={value}
          ref={googleAutocompleteRef}
          apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
          onBlur={() => setTouched(true)}
          onPlaceSelected={(place: any) => {
            if (!place) return;

            if (googleAutocompleteRef && googleAutocompleteRef.current) {
              googleAutocompleteRef.current.value = place.formatted_address;
            }
            onChange(place.formatted_address);

            // Address have error if no zip code is returned
            const postalCodeComponent = getGoogleAddressComponent(
              ['postal_code'],
              place.address_components
            );
            if (!postalCodeComponent) {
              setHaveError && setHaveError(true);
            } else {
              const address = getNameFromGoogleAddressComponents(
                'address',
                place.address_components
              );
              const city = getNameFromGoogleAddressComponents(
                'city',
                place.address_components
              );
              const zipCode = getNameFromGoogleAddressComponents(
                'zipCode',
                place.address_components
              );
              const streetNumber = getNameFromGoogleAddressComponents(
                'streetNumber',
                place.address_components
              );
              setAddressComponents &&
                setAddressComponents({
                  street: `${address} ${streetNumber}`,
                  city,
                  zip: zipCode,
                  country,
                });
              if (!address || !city || !zipCode || !streetNumber) {
                setHaveError && setHaveError(true);
              } else {
                setHaveError && setHaveError(false);
              }
            }
          }}
          className={`${
            touched && haveError ? 'border-b border-b-[#de2100]' : 'border-none'
          } w-full outline-none`}
          options={{
            types: ['address'],
            componentRestrictions: { country: country.toLowerCase() },
          }}
          onChange={() => setHaveError && setHaveError(true)}
        />
      ) : (
        <input
          className={`appearance-none block w-full text-gray-700 py-1 rounded-none leading-tight outline-none bg-white ${
            touched && haveError ? 'border-b border-b-[#de2100]' : 'border-none'
          }`}
          id={id}
          type={type}
          placeholder={t(`${translateKeyPre}${placeholderKey}`)}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setTouched(true);
          }}
          data-zuko-id={zukoId}
          maxLength={maxLength}
          onBlur={() => setTouched(true)}
        />
      )}
    </>
  );
};

const getGoogleAddressComponent = (types: string[], addressComponents: any) => {
  return addressComponents.find((component: any) =>
    types.some((type: string) => component.types.includes(type))
  );
};

const getNameFromGoogleAddressComponents = (
  type: 'address' | 'city' | 'state' | 'zipCode' | 'streetNumber',
  addressComponents: any
): string => {
  let result = '';
  switch (type) {
    case 'address':
      const streetName = getGoogleAddressComponent(
        ['route'],
        addressComponents
      );
      result = streetName.short_name;
      break;
    case 'city':
      const cityComp = getGoogleAddressComponent(
        ['locality'],
        addressComponents
      );
      if (cityComp) result = cityComp.long_name;
      break;
    case 'state':
      const stateComp = getGoogleAddressComponent(
        ['administrative_area_level_1'],
        addressComponents
      );
      result = stateComp.short_name;
      break;
    case 'zipCode':
      const zipCodeComp = getGoogleAddressComponent(
        ['postal_code'],
        addressComponents
      );
      result = zipCodeComp.long_name;
      break;
    case 'streetNumber':
      const streetNumberComponent = getGoogleAddressComponent(
        ['street_number'],
        addressComponents
      );
      result = streetNumberComponent?.long_name;
      break;
    default:
      break;
  }

  return result;
};

export const Footer = () => {
  const { t } = useTranslation();

  const links = [
    {
      url: 'https://www.facebook.com/EnphaseEnergy/',
      icon: iconFacebook,
      zukoId: 'link-facebook',
    },
    {
      url: 'https://www.youtube.com/user/EnphaseEnergy/',
      icon: iconYoutube,
      zukoId: 'link-youtube',
    },
    {
      url: 'https://twitter.com/Enphase/',
      icon: iconTwitter,
      zukoId: 'link-twitter',
    },
    {
      url: 'https://www.linkedin.com/company/enphase-energy/',
      icon: iconLinkedIn,
      zukoId: 'link-linkedin',
    },
  ];

  return (
    <div className="p-6 md:px-20 bg-white text-[11px] md:text-[12px] absolute w-full bottom-0 z-10">
      <div className="max-w-[1060px] m-auto">
        <div className="flex justify-between">
          <div className="flex">
            {links.map((link, index) => (
              <a
                key={index}
                className="w-8"
                target="_blank"
                href={link.url}
                rel="noreferrer"
                data-zuko-id={link.zukoId}>
                <img
                  className="social-icon m-auto w-full"
                  src={link.icon}
                  alt="social"
                />
              </a>
            ))}
          </div>
          <button
            type="button"
            className="text-enph-grey-02"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            data-zuko-id="button-back-to-top">
            <span>back to top</span>
            <img
              alt="back to top"
              src={iconCaret}
              className="inline-block ml-[10px]"></img>
          </button>
        </div>
        <DividerLine smallMarging={true}></DividerLine>
        <div className="flex justify-between flex-col lg:flex-row text-center gap-y-4 lg:gap-y-0 mt-[20px] text-enph-grey-02 lg:text-enph-dark-grey">
          <div>
            Copyright © {new Date().getFullYear()} Enphase Energy.{' '}
            {t('globalFunnel.footer.allRightsReserved')}.
          </div>
          <div className="flex flex-col lg:flex-row gap-y-4 lg:gap-y-0">
            <div>
              <a
                href={t('globalFunnel.footer.termsLink')}
                target="_blank"
                rel="noreferrer"
                className="mx-[8px]">
                {t('globalFunnel.footer.terms')}
              </a>
              <a
                href={t('globalFunnel.footer.privacyLink')}
                target="_blank"
                rel="noreferrer"
                className="mx-[8px]">
                {t('globalFunnel.footer.privacy')}
              </a>
            </div>
            <div>
              <button
                type="button"
                onClick={() => sp.showPrivacyBanner()}
                className="mx-[8px]"
                data-zuko-id="button-settings-dont-share-my-info">
                Settings / Do Not Sell or Share My Personal Information
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
